import React, { useEffect, useState } from 'react';
import { API_BASE, withKey, formatNumber } from '../services/utils';
import { fetchLatestRatesWithMarkups, fetchMarkups } from '../services/markupsAPI';
import { FaCircleXmark } from "react-icons/fa6";

function ExchangeRateModal({ show, currencyRates, currentRates, offer, onClose, onSnapshotUpdated, onRatesUpdated, isReadOnly }) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [markupByCurrency, setMarkupByCurrency] = useState({});

  const nonDkkItems = (offer?.items || []).filter(item => item?.costcurr && item.costcurr !== 'DKK');
  const fxDateTime = offer?.fx_fetched_at || '';
  const fxDate = fxDateTime ? new Date(fxDateTime).toLocaleDateString('en-GB') : '—';

  const combinedByCurrency = nonDkkItems.reduce((acc, item) => {
    const currency = item.costcurr;
    if (!currency || currency === 'DKK') return acc;
    const localCost = Number(item.local_cost) || 0;
    acc[currency] = (acc[currency] || 0) + localCost;
    return acc;
  }, {});

  useEffect(() => {
    let cancelled = false;

    const loadMarkups = async () => {
      try {
        const data = await fetchMarkups();
        if (cancelled) return;
        const map = {};
        for (const entry of (data?.markups || [])) {
          const currency = (entry?.currency || '').toString().toUpperCase();
          if (!currency) continue;
          map[currency] = Number(entry?.markup_pct || 0);
        }
        setMarkupByCurrency(map);
      } catch (error) {
        console.error('Failed to load markups for exchange rate modal:', error);
        if (!cancelled) setMarkupByCurrency({});
      }
    };

    loadMarkups();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!show) return null;

  const rows = Object.entries(combinedByCurrency).map(([currency, localCost]) => {
    const snapshotRateRaw = Number(currencyRates?.[currency]) || 0;
    const currentRateRaw = Number(currentRates?.[currency]) || snapshotRateRaw;
    const markupPct = Number(markupByCurrency?.[currency] || 0);
    const currentRateWithMarkupRaw = currentRateRaw * (1 + (markupPct / 100));
    const snapshotRate = snapshotRateRaw ? snapshotRateRaw / 100 : 0;
    const currentRate = currentRateWithMarkupRaw ? currentRateWithMarkupRaw / 100 : 0;

    return {
      id: currency,
      currency,
      localCost,
      snapshotRate,
      currentRate,
    };
  });

  const handleUpdateUsedRates = async () => {
    if (isReadOnly) return;
    if (!offer?.id) return;
    const confirmed = window.confirm('Update used rates to current rates (with global markups)? This will overwrite the saved snapshot.');
    if (!confirmed) return;

    setIsUpdating(true);
    try {
      const data = await fetchLatestRatesWithMarkups();
      if (!data || !data.marked_up_rates) {
        window.alert('No current rates available.');
        return;
      }
      const markedUpRates = data.marked_up_rates;
      const nowIso = new Date().toISOString();
      const payload = {
        fx_rates: markedUpRates,
        fx_fetched_at: nowIso,
        fx_source: data.source || 'Nationalbanken',
        fx_locked_at: nowIso,
      };
      const url = await withKey(`${API_BASE}/api/localdb/offers/${offer.id}`);
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to update used rates. Status: ' + response.status);
      }

      if (onRatesUpdated) onRatesUpdated(data.rates || markedUpRates);
      if (onSnapshotUpdated) onSnapshotUpdated(payload);
    } catch (error) {
      console.error('Update used rates failed:', error);
      window.alert('Failed to update used rates.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-8" aria-hidden={false}>
      <div className="mt-[50px] w-full max-w-5xl rounded-2xl border border-black/10 bg-white shadow-xl" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between gap-4 border-b border-black/10 px-6 py-4">
          <h3 className="text-base font-semibold text-black/80">Exchange Rate</h3>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-black/60 transition hover:border-company-main/40 hover:text-company-main"
            onClick={onClose}
            aria-label="Close exchange rate"
          >
            <FaCircleXmark className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              <div className="text-xs font-semibold tracking-wide text-black/50">
                Original rate date: <span className="font-semibold text-black/70">{fxDate}</span>
              </div>
              <button
                type="button"
                className="rounded-lg bg-company-main px-3 py-2 text-xs font-semibold text-white transition hover:brightness-95 shadow-lg ml-auto disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleUpdateUsedRates}
                disabled={isUpdating || !offer?.id || isReadOnly}
              >
                {isUpdating ? 'Updating...' : 'Pull New Exchange Rates'}
              </button>
            </div>
            
          </div>

          <div className="overflow-x-auto rounded-xl border border-black/10">
            <table className="w-full table-fixed border-collapse text-xs">
              <thead>
                <tr>
                  <th className="whitespace-nowrap bg-white px-3 py-2 text-left text-[12px] font-medium tracking-wide text-black">Currency</th>
                  <th className="whitespace-nowrap bg-white px-3 py-2 text-left text-[12px] font-medium tracking-wide text-black">Amount</th>
                  <th className="whitespace-nowrap bg-white px-3 py-2 text-left text-[12px] font-medium tracking-wide text-black">Used Rate</th>
                  <th className="whitespace-nowrap bg-white px-3 py-2 text-left text-[12px] font-medium tracking-wide text-black">Current Rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-sm text-black/50">
                      No non-DKK line items found.
                    </td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 1 ? 'bg-company-toned' : 'bg-white'}>
                    <td className="border-b border-black/5 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        value={row.currency}
                        className="w-full rounded-md border border-black/10 bg-black/5 px-2 py-1 text-xs font-semibold text-black/80"
                      />
                    </td>
                    <td className="border-b border-black/5 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        value={formatNumber(row.localCost, 2)}
                        className="w-full rounded-md border border-black/10 bg-black/5 px-2 py-1 text-right text-xs text-black/70"
                      />
                    </td>
                    <td className="border-b border-black/5 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        value={row.snapshotRate ? formatNumber(row.snapshotRate, 4) : '—'}
                        className="w-full rounded-md border border-black/10 bg-black/5 px-2 py-1 text-right text-xs text-black/70"
                      />
                    </td>
                    <td className="border-b border-black/5 px-3 py-2">
                      <input
                        type="text"
                        readOnly
                        value={row.currentRate ? formatNumber(row.currentRate, 4) : '—'}
                        className="w-full rounded-md border border-black/10 bg-black/5 px-2 py-1 text-right text-xs text-black/70"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExchangeRateModal;
